import { Vue, Ref, Prop } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { BModal } from 'bootstrap-vue/esm/components/modal';
import TimelapseFilter from '@/components/timelapsefilter/timelapsefilter.component.vue';
import TimelapseSettings from '@/components/timelapsesettings/timelapsesettings.component.vue';
import { Camera } from '@/models/camera';
import S3Image from '@/components/s3-image/S3Image.vue';
import { Project } from '@/models/project';
import { Blur } from '@/models/blur';
import { Area } from '@/models/area';
import { Photo } from '@/models/photo';
import { Storage } from 'aws-amplify';
import VueSlider from 'vue-slider-component';
import { State, Action } from 'vuex-class';
import PremiumOverlay from '../premiumoverlay/premiumoverlay.component.vue';
import { Logger } from 'aws-amplify';
const logger = new Logger('BlurModal');

interface Point {
  x: number;
  y: number;
}

interface Polygon extends Area {
  points: Point[];
  color: string;
}

interface BlurForm extends Blur {
  photoBegin?: Photo;
  photoEnd?: Photo;
}

@Component({
  components: {
    TimelapseFilter,
    TimelapseSettings,
    S3Image,
    VueSlider,
    PremiumOverlay,
  },
})
export default class BlurModal extends Vue {
  get dateFormat(): string {
    return this.locale === 'en' ? 'MM/DD/YY' : 'DD/MM/YY';
  }

  get dateFormatWithHour(): string {
    return this.locale === 'en' ? 'MM/DD/YY HH:mm' : 'DD/MM/YY HH:mm';
  }

  get blurToApply(): Blur[] {
    logger.info('compute blurToApply');
    return this.blurs.filter((blur) => {
      const date = new Date(this.photo.createdAt).getTime() / 1000;
      const begin = new Date((blur.begin ?? 0) * 1000).getTime() / 1000;
      if (blur.photo) {
        return blur.photo === this.photo.id;
      } else if (blur.noEnd) {
        return begin <= date;
      } else {
        const end = new Date((blur.end ?? 0) * 1000).getTime() / 1000;
        return begin <= date && date <= end;
      }
    });
  }
  @Prop({ type: Object, required: true }) readonly camera!: Camera;
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Array, required: true }) readonly photos!: Photo[];
  @Prop({ type: Object, required: true }) readonly value!: Photo;
  @Prop({ required: true }) readonly isPremium!: boolean;
  // private photo!: Photo;
  private get photo(): Photo {
    return this.value;
  }
  private set photo(photo: Photo) {
    if (JSON.stringify(photo) !== JSON.stringify(this.value)) {
      this.$emit('input', photo);
      this.preDraw(photo);
      this.mainSliderValue = photo; // Mettre à jour le slider avec la photo affichée
    }
  }

  @Ref('blur-modal') readonly modal!: BModal;

  @State('locale', { namespace: 'profile' }) readonly locale!: string;
  @Action('loadBlurProject', { namespace: 'project' }) loadBlurProject!: (data: {
    projectId: string;
    cameraId: string;
  }) => Promise<Blur[]>;
  @Action('saveBlurProject', { namespace: 'project' }) saveBlurProject!: (data: {
    projectId: string;
    cameraId: string;
    blurs: Blur[];
  }) => Promise<Blur[]>;
  private loadingBlur = false;

  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  private drag = false;
  private imageObj: HTMLImageElement = new Image();

  private blurs: BlurForm[] = [];
  private currentBlurIndex = -1;
  private mainSliderValue: Photo | null = null;
  private mainSliderIsDragging = false;
  private zoomEnabled = false;

  /*
  @Watch('photo', { deep: true })
  onModelChange(photo: Photo): void {
    if (JSON.stringify(photo) !== JSON.stringify(this.value)) {
      logger.info('onModelChange', photo.key);
      this.$emit('input', photo);
      this.preDraw();
    }
  }
  @Watch('value', { deep: true })
  onValueChange(value: Photo): void {
    logger.info('onValueChange');
    this.photo = Object.create(value);
  }
  */

  marks(val: Photo | undefined): number | unknown {
    if (val === undefined) {
      return 0;
    } else {
      const blur = this.blurs.find((b) => b.photo === val.id);
      if (blur === undefined) {
        return 0;
      } else {
        return {
          label: '',
          style: {
            width: '8px',
            height: '8px',
            display: 'block',
            backgroundColor: blur.area as Polygon,
            transform: 'translate(-2px, -2px)',
          },
          labelStyle: {
            color: 'red',
          },
        };
      }
    }
  }

  mounted(): void {
    logger.info('mounted');
    if (this.photos.length > 0) {
      this.mainSliderValue = this.photo; // Initialiser le slider avec la photo actuellement affichée
    }
    this.loadBlurProject({ projectId: this.project.id, cameraId: this.camera.id }).then((data) => {
      this.loadingBlur = false;
      this.blurs = data.map((blur: Blur) => {
        const photoBegin = this.photos.find((p) => new Date(p.createdAt).getTime() / 1000 === blur.begin);
        const photoEnd = this.photos.find((p) => blur.end && new Date(p.createdAt).getTime() / 1000 === blur.end);

        const factor = (() => {
          if (this.camera && this.camera.optic) {
            const width = parseInt(this.camera.optic.split('x')[0], 10);
            switch (this.camera.optic) {
              case '7360x2650':
                return 5.52552552552;
              case '4624x3472':
                return 3.47147147147;
              case '2664x1998':
                return 2;
              case '4260x1534':
                return 3.198198198198;
              default:
                return width / 1332; // Calcul dynamique basé sur la largeur de l'image
            }
          } else {
            return 1; // Valeur par défaut si this.camera ou this.camera.optic est undefined
          }
        })();
        // Diviser les coordonnées x et y par le facteur
        const adjustedPoints = blur.area.points.map((point) => ({
          x: point.x / factor,
          y: point.y / factor,
        }));

        console.log('Blur loaded:', blur);
        return {
          ...blur,
          photoBegin,
          photoEnd,
          area: {
            ...blur.area,
            points: adjustedPoints, // Mettre à jour les points avec les coordonnées ajustées
            color: this.getRandomColor(),
          },
        };
      });
    });

    this.canvas = document.getElementById('blur-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d');

    this.canvas.addEventListener('click', this.handleCanvasClick, false);
    this.canvas.addEventListener('contextmenu', this.handleContextMenu, false);
    this.canvas.addEventListener('mousemove', this.handleMouseMove, false);
    this.canvas.addEventListener('mouseleave', this.handleMouseLeave, false);
  }

  handleMouseMove(e: MouseEvent): void {
    if (!this.zoomEnabled) return;

    const zoomContainer = document.getElementById('zoom-container') as HTMLElement;
    const zoomCanvas = document.getElementById('zoom-canvas') as HTMLCanvasElement;
    const zoomCtx = zoomCanvas.getContext('2d');

    if (this.ctx && this.canvas && zoomCtx) {
      const rect = this.canvas.getBoundingClientRect();
      const mousePos = this.getMousePos(e);
      const zoomSize = 75; // Taille de la zone à zoomer

      // Positionner la bulle de zoom près du curseur
      zoomContainer.style.display = 'block';
      zoomContainer.style.left = `${e.clientX - rect.left - zoomContainer.clientWidth / 2}px`;
      zoomContainer.style.top = `${e.clientY - rect.top - zoomContainer.clientHeight / 2}px`;

      // Effacer le contenu précédent
      zoomCtx.clearRect(0, 0, zoomCanvas.width, zoomCanvas.height);

      // Centrer l'image dans la bulle de zoom
      zoomCtx.drawImage(
        this.canvas,
        mousePos.x - zoomSize / 2 + 18,
        mousePos.y - zoomSize / 2 + 18,
        zoomSize,
        zoomSize,
        0,
        0,
        zoomCanvas.width,
        zoomCanvas.height
      );

      // Dessiner une petite croix au centre du zoomCanvas
      const crossSize = 2; // Taille de la croix (longueur des bras)
      const centerX = zoomCanvas.width / 2 - 50;
      const centerY = zoomCanvas.height / 2 - 50;

      // Ligne horizontale de la croix
      zoomCtx.beginPath();
      zoomCtx.moveTo(centerX - crossSize / 2, centerY);
      zoomCtx.lineTo(centerX + crossSize / 2, centerY);
      zoomCtx.strokeStyle = '#5fc9e4'; // Couleur de la croix
      zoomCtx.lineWidth = 40; // Épaisseur de la croix
      zoomCtx.stroke();

      // Ligne verticale de la croix
      zoomCtx.beginPath();
      zoomCtx.moveTo(centerX, centerY - crossSize / 2);
      zoomCtx.lineTo(centerX, centerY + crossSize / 2);
      zoomCtx.strokeStyle = '#5fc9e4'; // Couleur de la croix
      zoomCtx.lineWidth = 40; // Épaisseur de la croix
      zoomCtx.stroke();
    }
  }

  handleMouseLeave(): void {
    const zoomContainer = document.getElementById('zoom-container') as HTMLElement;
    zoomContainer.style.display = 'none';
  }

  handleCanvasClick(e: MouseEvent): void {
    // Obtenir la position de la souris et arrondir les valeurs
    const { x, y } = this.getMousePos(e);
    const roundedX = Math.round(x);
    const roundedY = Math.round(y);
    if (this.currentBlurIndex !== -1) {
      const currentBlur = this.blurs[this.currentBlurIndex];
      // Vérifiez si l'area est initialisée
      if (!currentBlur.area) {
        currentBlur.area = { points: [], color: this.getRandomColor() } as Polygon;
      }
      // Vérifiez si les points sont initialisés
      if (!Array.isArray(currentBlur.area.points)) {
        currentBlur.area.points = []; // Initialisez le tableau de points si ce n'est pas déjà fait
      }
      // Ajouter les points arrondis au tableau
      console.log(`Adding point to polygon: (${roundedX}, ${roundedY})`);
      currentBlur.area.points.push({ x: roundedX, y: roundedY });
      console.log('Current polygon points:', currentBlur.area.points);
      this.draw();
    } else {
      console.error('currentBlurIndex is invalid:', this.currentBlurIndex);
    }
  }

  handleContextMenu(e: MouseEvent): void {
    e.preventDefault();
    this.completePolygon();
  }
  completePolygon(): void {
    if (this.currentBlurIndex !== -1) {
      const currentBlur = this.blurs[this.currentBlurIndex];
      if (currentBlur && Array.isArray(currentBlur.area.points) && currentBlur.area.points.length > 2) {
        currentBlur.area.points.push(currentBlur.area.points[0]); // Ferme le polygone en reliant le dernier point au premier
        this.draw();
        this.drag = false;
      } else {
        console.error('Polygon cannot be completed', currentBlur.area);
      }
    }
  }

  show(): void {
    this.modal.show();
  }

  onShown(): void {
    logger.info('onShown');
    if (this.canvas === null) {
      this.canvas = document.getElementById('blur-canvas') as HTMLCanvasElement;
      this.mainSliderValue = this.photos[0];
      this.ctx = this.canvas.getContext('2d');

      this.canvas.addEventListener(
        'mousedown',
        (e) => {
          logger.info('mousedown');
          if (this.currentBlurIndex !== -1) {
            const { x, y } = this.getMousePos(e);
            const blurArea = this.blurs[this.currentBlurIndex].area;

            if (blurArea.points.length > 0) {
              // Modifier le premier point, ou un point spécifique
              blurArea.points[0].x = x;
              blurArea.points[0].y = y;
            } else {
              // Ajouter un nouveau point si le tableau est vide
              blurArea.points.push({ x: 0, y: 0 });
              blurArea.points[0].x = x;
              blurArea.points[0].y = y;
            }

            this.drag = true;
          }
        },
        false
      );

      this.canvas.addEventListener(
        'mouseup',
        () => {
          logger.info('mouseup');
          this.drag = false;
        },
        false
      );
      this.canvas.addEventListener('mousemove', this.handleMouseMove, false);

      this.changeCurrentBlur(0);
    }
    if (this.photo !== null) {
      this.mainSliderValue = this.photo; // Synchroniser le slider avec la photo affichée
      this.$nextTick().then(() => this.preDraw(this.photo)); // Redessiner la photo affichée
    }
  }

  close(): void {
    this.loadingBlur = false;
    logger.info('close');
    this.modal.hide();
  }

  private prevPhoto(): void {
    const currentIndex = this.photos.indexOf(this.mainSliderValue as Photo);
    if (currentIndex > 0) {
      this.mainSliderValue = this.photos[currentIndex - 1];
      this.photo = this.mainSliderValue; // Mettre à jour la photo affichée
    }
  }

  private nextPhoto(): void {
    const currentIndex = this.photos.indexOf(this.mainSliderValue as Photo);
    if (currentIndex < this.photos.length - 1) {
      this.mainSliderValue = this.photos[currentIndex + 1];
      this.photo = this.mainSliderValue; // Mettre à jour la photo affichée
    }
  }

  private get isFirstPhoto(): boolean {
    return this.photos.indexOf(this.mainSliderValue as Photo) === 0;
  }

  private get isLastPhoto(): boolean {
    return this.photos.indexOf(this.mainSliderValue as Photo) === this.photos.length - 1;
  }

  private getMousePos(evt: MouseEvent): { x: number; y: number } {
    if (this.canvas === null) {
      logger.warn('no canvas');
      return { x: 0, y: 0 };
    }
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((evt.clientX - rect.left) / (rect.right - rect.left)) * this.canvas.width,
      y: ((evt.clientY - rect.top) / (rect.bottom - rect.top)) * this.canvas.height,
    };
  }

  private async preDraw(photo: Photo): Promise<void> {
    logger.info('preDraw', photo.mediumKey);
    if (photo.mediumKey !== undefined) {
      return Storage.get(photo.mediumKey).then((signedUrl) => {
        this.imageObj = new Image();
        this.imageObj.crossOrigin = 'Anonymous';
        logger.info(signedUrl);
        this.imageObj.onload = () => {
          logger.info('image loaded');
          this.draw();
        };
        this.imageObj.src = signedUrl as string;
      });
    }
  }

  private draw(): void {
    if (this.ctx !== null && this.canvas !== null) {
      const canvasWidth = this.canvas.clientWidth;
      const canvasHeight = this.canvas.clientHeight;
      this.ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      this.ctx.beginPath();
      this.ctx.filter = 'none';
      this.drawImagFit(this.imageObj, this.ctx, this.canvas);
      this.blurToApply.forEach((blur) => {
        const area = blur.area as Polygon;
        if (this.ctx !== null && area && Array.isArray(area.points) && area.points.length > 1) {
          console.log('Drawing polygon with points:', area.points);
          this.ctx.beginPath();
          this.ctx.moveTo(area.points[0].x, area.points[0].y);
          for (let i = 1; i < area.points.length; i++) {
            this.ctx.lineTo(area.points[i].x, area.points[i].y);
          }
          this.ctx.closePath();
          this.ctx.strokeStyle = area.color;
          this.ctx.fillStyle = area.color;
          this.ctx.stroke();
          this.ctx.filter = `blur(10px)`;
          this.ctx.fill();
        } else {
          console.error('Polygon points are not properly initialized or are empty', area);
          console.log('Current polygon points:', area?.points);
        }
      });
    }
  }

  private drawImagFit(img: HTMLImageElement, ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) {
    // get the scale
    const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
    // get the top left position of the image
    const x = canvas.width / 2 - (img.width / 2) * scale;
    const y = canvas.height / 2 - (img.height / 2) * scale;
    ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
  }

  private getRandomColor() {
    let colors = [
      '#1abc9c',
      '#2ecc71',
      '#3498db',
      '#9b59b6',
      '#34495e',
      '#16a085',
      '#27ae60',
      '#2980b9',
      '#8e44ad',
      '#2c3e50',
      '#f1c40f',
      '#e67e22',
      '#e74c3c',
      '#ecf0f1',
      '#95a5a6',
      '#f39c12',
      '#d35400',
      '#c0392b',
      '#bdc3c7',
      '#7f8c8d',
    ];
    colors = colors.filter((c) => this.blurs.map((r) => (r.area as Polygon).color).indexOf(c) === -1);
    return colors[Math.floor(Math.random() * (colors.length - 1))];
  }

  private onSliderChange(blurIndex: number, e: Photo[] | Photo) {
    const blur = this.blurs[blurIndex];
    if (Array.isArray(e)) {
      blur.begin = new Date(e[0].createdAt).getTime() / 1000;
      blur.photoBegin = e[0];
      blur.end = new Date(e[1].createdAt).getTime() / 1000;
      blur.photoEnd = e[1];
    } else {
      blur.photoBegin = e;
      blur.begin = new Date(e.createdAt).getTime() / 1000;
    }
  }

  private onMainSliderChange(photo: Photo) {
    if (!this.mainSliderIsDragging) {
      this.photo = photo;
    }
  }
  private onMainSliderDragStart() {
    logger.info('onMainSliderDragStart');
    this.mainSliderIsDragging = true;
  }

  private onMainSliderDragEnd() {
    if (this.mainSliderValue !== null) {
      logger.info('onMainSliderDragEnd');
      this.mainSliderIsDragging = false;
      this.photo = this.mainSliderValue;
    }
  }

  private onNoEndChange(blurIndex: number, noEnd: boolean) {
    logger.info('onNoEndChange', blurIndex, noEnd);
    const blur = this.blurs[blurIndex];
    blur.noEnd = noEnd;
    if (noEnd) {
      const save1 = blur.begin;
      const save2 = blur.photoBegin;
      blur.begin = 0;
      blur.photoBegin = undefined;
      blur.end = undefined;
      blur.photoEnd = undefined;
      this.$nextTick().then(() => {
        blur.begin = save1;
        blur.photoBegin = save2;
      });
    }
  }

  private changeCurrentBlur(blurIndex: number) {
    logger.info('changeCurrentBlur', {
      blurIndex,
      valid: this.currentBlurIndex !== blurIndex && blurIndex < this.blurs.length,
    });
    if (this.currentBlurIndex !== blurIndex && blurIndex < this.blurs.length) {
      this.currentBlurIndex = blurIndex;
      const blur = this.blurs[blurIndex];
      let newPhoto: Photo | undefined;
      if (blur.photo) {
        newPhoto = this.photos.find((p) => p.id === blur.photo);
      } else {
        newPhoto = this.photos.find(
          (p) => new Date(p.createdAt).getTime() > new Date((blur.begin as number) * 1000).getTime()
        );
      }
      if (newPhoto !== undefined) {
        this.mainSliderValue = newPhoto;
        this.preDraw(newPhoto);
      }
    }
  }

  private addregion() {
    this.zoomEnabled = true;
    this.blurs.push({
      area: { points: [], color: this.getRandomColor() } as Polygon,
      begin: new Date(this.photos[0].createdAt).getTime() / 1000,
      photoBegin: this.photos[0],
      end: new Date(this.photos[this.photos.length - 1].createdAt).getTime() / 1000,
      photoEnd: this.photos[this.photos.length - 1],
      noEnd: false,
    });
    this.currentBlurIndex = this.blurs.length - 1;
  }

  private addphoto() {
    this.zoomEnabled = true;
    this.blurs.push({
      area: { points: [], color: this.getRandomColor() } as Polygon,
      photo: this.photo?.id,
      noEnd: false,
    });
    this.currentBlurIndex = this.blurs.length - 1;
  }

  private cleanBlurData(blur: BlurForm): any {
    // Cast `blur.area` to `any` temporairement pour permettre l'accès à la propriété `color`
    const area = blur.area as any;

    // Créer un nouvel objet sans la propriété `color`
    const factor = (() => {
      if (this.camera && this.camera.optic) {
        const width = parseInt(this.camera.optic.split('x')[0], 10);
        switch (this.camera.optic) {
          case '7360x2650':
            return 5.52552552552;
          case '4624x3472':
            return 3.47147147147;
          case '2664x1998':
            return 2;
          case '4260x1534':
            return 3.198198198198;
          default:
            return width / 1332; // Calcul dynamique basé sur la largeur de l'image
        }
      } else {
        return 1; // Valeur par défaut si this.camera ou this.camera.optic est undefined
      }
    })();
    const cleanedArea = {
      points: area.points.map((point: Point) => ({
        x: point.x * factor,
        y: point.y * factor,
      })),
    };

    return {
      ...blur,
      area: cleanedArea, // Assigner l'objet `area` nettoyé
      noEnd: blur.noEnd,
      photo: blur.photo,
    };
  }
  private save() {
    logger.info('save');
    this.loadingBlur = true;
    const cleanedBlurs = this.blurs.map((blur) => this.cleanBlurData(blur));
    try {
      cleanedBlurs.forEach(this.validateBlur);
    } catch (error) {
      this.loadingBlur = false;
      console.error('Validation error:', error);
      return;
    }
    const requestBody = {
      projectId: this.project.id,
      cameraId: this.camera.id,
      blurs: cleanedBlurs, // Use the cleaned data
    };
    this.saveBlurProject(requestBody)
      .then(() => {
        this.loadingBlur = false;
        this.close();
      })
      .catch((error) => {
        this.loadingBlur = false;
        console.error('Error saving blur project:', error);
      });
  }

  private deleteBlur(index: number): void {
    if (index >= 0 && index < this.blurs.length) {
      this.blurs.splice(index, 1); // Supprimer la blur de la liste
      this.currentBlurIndex = -1; // Réinitialiser l'indice actuel de blur
      this.draw(); // Redessiner le canvas pour mettre à jour l'affichage
    } else {
      console.error('Invalid blur index:', index);
    }
  }

  private validateBlur(blur: Blur): void {
    if (blur.noEnd !== undefined && typeof blur.noEnd !== 'boolean') {
      throw new Error('Invalid noEnd flag');
    }
    if (blur.area) {
      blur.area.points.forEach((point) => {
        if (typeof point.x !== 'number' || typeof point.y !== 'number') {
          throw new Error('Invalid point coordinates');
        }
      });
    } else {
      throw new Error('Blur must have an area');
    }
  }
}
